<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminDomainListItem, type PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminDomainsList' })

const { t } = useI18n()
const router = useRouter()

const loading = ref(true)
const domains = ref<AdminDomainListItem[]>([])
const statusFilter = ref<string>('all')
const searchQuery = ref('')
const searchDebounce = ref<ReturnType<typeof setTimeout> | null>(null)

const pagination = usePagination({ initialItemsPerPage: 50 })

const statusOptions = computed(() => [
  { value: 'all', title: t('dashboard.admin.domains.status.all') },
  { value: 'active', title: t('dashboard.admin.domains.status.active') },
  { value: 'pending_delete', title: t('dashboard.admin.domains.status.pendingDelete') },
  { value: 'deleted', title: t('dashboard.admin.domains.status.deleted') },
  { value: 'trade_out', title: t('dashboard.admin.domains.status.tradeOut') },
  { value: 'external', title: t('dashboard.admin.domains.status.external') },
  { value: 'suspended', title: t('dashboard.admin.domains.status.suspended') },
  { value: 'transfer_pending', title: t('dashboard.admin.domains.status.transferPending') }
])

async function fetchDomains() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  let url = `/admin/domains?limit=${limit}&offset=${offset}`
  if (statusFilter.value !== 'all') {
    url += `&status=${statusFilter.value}`
  }
  if (searchQuery.value.trim()) {
    url += `&search=${encodeURIComponent(searchQuery.value.trim())}`
  }

  try {
    const res = await useAxios<null, PaginatedResponse<AdminDomainListItem>>(
      'GET',
      url,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminDomainListItem.array().safeParse(res.data.data)
      if (validate.success) {
        domains.value = validate.data
      } else {
        domains.value = res.data.data as AdminDomainListItem[]
      }
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminDomainsList' })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, () => {
  fetchDomains()
})

watch(statusFilter, () => {
  pagination.goToPage(1)
  fetchDomains()
})

watch(searchQuery, () => {
  if (searchDebounce.value) {
    clearTimeout(searchDebounce.value)
  }
  searchDebounce.value = setTimeout(() => {
    pagination.goToPage(1)
    fetchDomains()
  }, 300)
})

const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + domains.value.length - 1, pagination.total.value))

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active'
    case 'pending_delete': return 'status-pending-delete'
    case 'deleted': return 'status-deleted'
    case 'trade_out': return 'status-trade-out'
    case 'external': return 'status-external'
    case 'suspended': return 'status-suspended'
    case 'transfer_pending': return 'status-transfer-pending'
    default: return ''
  }
}

function getStatusLabel(status: string): string {
  const opt = statusOptions.value.find(s => s.value === status)
  return opt?.title || status
}

function navigateToDetail(id: number) {
  router.push({ name: 'dashboardAdminDomainDetail', params: { id } })
}

onMounted(() => {
  fetchDomains()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.domains.title')"
    :subtitle="$t('dashboard.admin.domains.subtitle')"
    icon="mdi-web"
    icon-color="cyan"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchDomains"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('dashboard.admin.domains.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <v-btn-toggle
        v-model="statusFilter"
        mandatory
        density="compact"
        class="status-toggle"
      >
        <v-btn
          v-for="opt in statusOptions"
          :key="opt.value"
          :value="opt.value"
          variant="outlined"
          size="small"
        >
          {{ opt.title }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="domains.length === 0" class="empty-state">
      <v-icon icon="mdi-web-off" size="64" />
      <h3>{{ $t('dashboard.admin.domains.empty.title') }}</h3>
      <p>{{ $t('dashboard.admin.domains.empty.description') }}</p>
    </div>

    <!-- Domains Table -->
    <div v-else class="table-container">
      <table class="domains-table">
        <thead>
          <tr>
            <th>{{ $t('dashboard.admin.domains.table.domain') }}</th>
            <th>{{ $t('dashboard.admin.domains.table.status') }}</th>
            <th>{{ $t('dashboard.admin.domains.table.account') }}</th>
            <th>{{ $t('dashboard.admin.domains.table.registered') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="domain in domains"
            :key="domain.id"
            class="table-row"
            @click="navigateToDetail(domain.id)"
          >
            <td class="domain-cell">
              <span class="domain-name">{{ domain.name }}</span>
              <span v-if="domain.name !== domain.name_ace" class="domain-ace">{{ domain.name_ace }}</span>
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="getStatusClass(domain.status)">
                {{ getStatusLabel(domain.status) }}
              </span>
            </td>
            <td class="account-cell">
              <span class="account-email">{{ domain.account_email }}</span>
              <span class="account-id">#{{ domain.account_id }}</span>
            </td>
            <td class="date-cell">{{ formatDate(domain.registered) }}</td>
            <td class="action-cell">
              <v-btn icon variant="text" size="small">
                <v-icon icon="mdi-chevron-right" />
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ $t('dashboard.admin.domains.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.goToPage(1)"
        >
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.prevPage()"
        >
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">
          {{ pagination.page.value }} / {{ pagination.totalPages.value }}
        </span>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.nextPage()"
        >
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.goToPage(pagination.totalPages.value)"
        >
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Filters */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 280px;
  flex: 1;
}

.status-toggle {
  flex-wrap: wrap;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(200, 218, 255, 0.3);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

/* Table */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.domains-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
}

.domain-cell {
  .domain-name {
    display: block;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }

  .domain-ace {
    display: block;
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
    margin-top: 2px;
  }
}

.account-cell {
  .account-email {
    display: block;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .account-id {
    display: block;
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.5);
    margin-top: 2px;
  }
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.status-active {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  // Amber, not grey: the registration is still live and still the customer's.
  &.status-pending-delete {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-deleted {
    background: rgba(158, 158, 158, 0.15);
    color: #9e9e9e;
  }

  &.status-trade-out {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.status-external {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }

  &.status-suspended {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-transfer-pending {
    background: rgba(0, 176, 255, 0.15);
    color: #4fc3f7;
  }
}

.action-cell {
  width: 48px;
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .domains-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .domain-cell .domain-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .domain-cell .domain-ace {
    color: rgba(15, 23, 42, 0.5);
  }

  .account-cell .account-email {
    color: rgba(15, 23, 42, 0.8);
  }

  .account-cell .account-id {
    color: rgba(15, 23, 42, 0.5);
  }

  .date-cell {
    color: rgba(15, 23, 42, 0.6);
  }

  .empty-state {
    .v-icon {
      color: rgba(15, 23, 42, 0.2);
    }

    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}

/* Mobile */
@media (max-width: 768px) {
  .admin-domains-list {
    padding: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .domains-table {
    min-width: 600px;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
